import * as CryptoJS from 'crypto-js';
import { environment } from '../../environments/environment';
import { EncryptUtil } from './encrypt.provider.rsa';

export class SecurityProvider {
  static PASSPHRASE = "";
  static LETTERS = "AaBbCcDdEeFfGgHhIiJjKkLlMmNnOoPpQqRrSsTtUuVvWwXxYyZz";

  static hash = (message: any, algo: string = 'sha256') => {
    switch (algo) {
      case 'sha1':
        return CryptoJS.SHA1(message).toString();
      case 'sha256':
        return CryptoJS.SHA256(message).toString();
      default:
        return "";
    }
  }

  static encrypt = (message: any, passphrase: string) => {
    const key = CryptoJS.enc.Utf8.parse(passphrase.padEnd(16));
    const iv1 = CryptoJS.enc.Utf8.parse(passphrase.padEnd(16));

    const encrypted = CryptoJS.AES.encrypt(message, key, {
      keySize: 128 / 32,
      iv: iv1,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    });
    return encrypted.toString();
  }

  static encryptByUrl = (message: any, passphrase: string) => {
    const key = CryptoJS.enc.Utf8.parse(passphrase.padEnd(16)); // Ensure key is 16 bytes
    const iv1 = CryptoJS.enc.Utf8.parse(passphrase.padEnd(16)); // Ensure IV is 16 bytes

    const encrypted = CryptoJS.AES.encrypt(message, key, {
      keySize: 128 / 32,
      iv: iv1,
      mode: CryptoJS.mode.CBC, // Change to CBC mode
      padding: CryptoJS.pad.Pkcs7
    });
    return encrypted.toString().replaceAll("/","-").replaceAll("+","_");
  }

  static decrypt = (cipher: any, passphrase: string) => {
    const key = CryptoJS.enc.Utf8.parse(passphrase.padEnd(16)); // Ensure key is 16 bytes
    const iv1 = CryptoJS.enc.Utf8.parse(passphrase.padEnd(16)); // Ensure IV is 16 bytes

    const decrypted = CryptoJS.AES.decrypt(cipher, key, {
      keySize: 128 / 32,
      iv: iv1,
      mode: CryptoJS.mode.CBC, // Change to CBC mode
      padding: CryptoJS.pad.Pkcs7
    });
    return decrypted.toString(CryptoJS.enc.Utf8);
  }

  static decryptByUrl = (cipher: any, passphrase: string) => {
    const key = CryptoJS.enc.Utf8.parse(passphrase.padEnd(16)); // Ensure key is 16 bytes
    const iv1 = CryptoJS.enc.Utf8.parse(passphrase.padEnd(16)); // Ensure IV is 16 bytes

    const decrypted = CryptoJS.AES.decrypt(cipher.replaceAll("-", "/").replaceAll("_", "+"), key, {
      keySize: 128 / 32,
      iv: iv1,
      mode: CryptoJS.mode.CBC, // Change to CBC mode
      padding: CryptoJS.pad.Pkcs7
    });
    return decrypted.toString(CryptoJS.enc.Utf8);
  }

  static getCipherKey() {
    if (SecurityProvider.PASSPHRASE === null) {
      throw new Error('Passphrase is NULL');
    }
    return CryptoJS.MD5(SecurityProvider.PASSPHRASE).toString().substr(0, 16);
  }

  static getCipherKeyFromPassphrase(passphrase: string) {
    return CryptoJS.MD5(passphrase).toString().substr(0, 16);
  }

  static base64Encrypt(data: string): string {
    return CryptoJS.enc.Base64.stringify(
      CryptoJS.enc.Utf8.parse(data)
    );
  }

  static base64Decrypt(data: string): string {
    return CryptoJS.enc.Base64.parse(data).toString(CryptoJS.enc.Utf8);
  }

  static hashJson = (json: any) => {
    if (!!json) {
      let excludeNull = (data: any) => {
        for (let x in data) {
          if (data[x] === undefined || data[x] === null) {
            delete data[x];
            continue;
          }
          if (typeof data[x] === 'object') {
            excludeNull(data[x]);
          }
        }
      }
      //excludeNull(json);
      return SecurityProvider.hash(JSON.stringify(json));
    }
    return "";
  }

  static setPassphrase(passphrase: string) {
    SecurityProvider.PASSPHRASE = passphrase;
  }

  static encPass(password: any): string {
    const iv = CryptoJS.lib.WordArray.random(128 / 8).toString(CryptoJS.enc.Hex);
    const salt = CryptoJS.lib.WordArray.random(128 / 8).toString(CryptoJS.enc.Hex);
    const randLet = SecurityProvider.LETTERS.charAt(Math.floor(Math.random() * 53));
    const randNum = Math.floor(Math.random() * 10);
    const ciphertext = this.aesEncrypt(salt, iv, randLet.concat(randNum.toString()), password, 128, 1000);
    const aesPassword = (iv.concat(randNum.toString()) + "::" + salt.concat(randLet) + "::" + ciphertext);
    return btoa(aesPassword);
  }

  static aesEncrypt(salt: any, iv: any, passPhrase: any, plainText: any, size: any, iteration: any) {
    const keySize = size / 32;
    const key = this.aesGenerateKey(salt, passPhrase, keySize, iteration);
    const encrypted = CryptoJS.AES.encrypt( plainText, key, { 
      iv: CryptoJS.enc.Hex.parse(iv) 
      }
    );
    return encrypted.ciphertext.toString(CryptoJS.enc.Base64);
  }

  static getUserDecoded = async () =>{
    return EncryptUtil.decryptAES(localStorage.getItem(environment.constants.mailinfo)!, environment.secretKey,"52015cf6259abf91c1db166587d67cd2","db4ace4d7223d0cce78f5f36376cafd5");
  }

  static getEmailPerson = async (): Promise<string> => {
    return new Promise<string>((resolve, reject) => {
      resolve(localStorage.getItem(environment.constants.mailinfo) ?? "");
    });
  }

  // static setUserDecoded(userKey:string) {
  //   if(userKey == "") {
  //     return "";
  //   }
  //   localStorage.setItem(environment.constants.user,this.encryptByUrl(userKey, environment.secretKey));
  //   return ;
  // }

  // static getDateLegibleSaved() {
  //   return SecurityProvider.decrypt(localStorage.getItem(environment.constants.lastHourLogged) ?? "", environment.secretKey);
  // }

  static getTimeLegible() {
    const now = new Date();
    const hours = now.getHours();
    const minutes = now.getMinutes();
    const seconds = now.getSeconds();
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }

  // static getUserDecoded() {
  //   var userToken = localStorage.getItem(environment.constants.user)??"";
  //   if(userToken == "") {
  //     return "";
  //   }
  //   return this.decryptByUrl(userToken, environment.secretKey);
  // }

  static aesGenerateKey(salt: any, passPhrase: any, keySize: any, iterationCount: any) {
    const key = CryptoJS.PBKDF2(
      passPhrase,
      CryptoJS.enc.Hex.parse(salt),
      { keySize: keySize, iterations: iterationCount });
    return key;
  }
}
